// Files on a record (Keel addition; the prototype has none). Dropped into any detail page as
// `<Attachments entityType="Invoice" entityId={id} />`; who may see and change them is decided by
// the module that owns the record, so this component only renders what the API allows.
import { type ApiError, del, getBlob, postForm, put, useAction, useApi, useAuth } from "@keel/core";
import { type DragEvent, useRef, useState } from "react";
import { confirmDialog, Icon } from "./ui";

export interface Attachment {
  id: string;
  entityType: string;
  entityId: string;
  fileName: string;
  contentType: string;
  byteSize: number;
  checksum: string;
  description: string | null;
  isInternal: boolean;
  uploadedBy: string | null;
  createdAt: string;
}

interface AttachableType {
  entityType: string;
  label: string;
  module: string;
  canWrite: boolean;
}

/** 1.2 MB, 940 kB, 3 bytes — short enough to sit in a row. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ICONS: [RegExp, string][] = [
  [/pdf/, "invoice"],
  [/^image\//, "image"],
  [/csv|spreadsheet|excel/, "grid"],
];
const iconFor = (contentType: string, fileName: string) => {
  const hay = `${contentType} ${fileName.toLowerCase()}`;
  return ICONS.find(([re]) => re.test(hay))?.[1] ?? "file";
};

export function Attachments({
  entityType,
  entityId,
  title = "Files",
  hint,
}: {
  entityType: string;
  entityId: string;
  title?: string;
  hint?: string;
}) {
  const { me } = useAuth();
  const { run, busy } = useAction();
  const types = useApi<AttachableType[]>("/attachments/types").data;
  const path = `/attachments?entityType=${entityType}&entityId=${entityId}`;
  const files = useApi<Attachment[]>(entityId ? path : null).data;
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [editing, setEditing] = useState<Attachment | null>(null);
  const kind = types?.find((t) => t.entityType === entityType);
  // The API decides; until it has answered, show nothing rather than a panel that may not apply.
  if (!types || !kind) return null;
  const canWrite = kind.canWrite;

  const send = async (list: FileList | null) => {
    if (!list?.length) return;
    for (const f of Array.from(list)) {
      const form = new FormData();
      form.set("entityType", entityType);
      form.set("entityId", entityId);
      form.set("file", f, f.name);
      await run(
        () => postForm("/attachments", form),
        // Named, because uploading several at once and getting one "Uploaded" tells you nothing.
        `${f.name} uploaded`,
      );
    }
    if (input.current) input.current.value = "";
  };

  const open = async (a: Attachment) => {
    // The content needs the session's Authorization header, so it cannot be a plain link; fetch it
    // and hand the browser a blob with the original name.
    try {
      const blob = await getBlob(`/attachments/${a.id}/content`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.fileName;
      link.click();
      // Revoked late: Safari has not finished with the URL when `click()` returns.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      await run(() => Promise.reject(e as ApiError));
    }
  };

  return (
    <section className="card">
      <header className="card-head">
        <div>
          <h2>
            {title}
            {files && files.length > 0 && <span className="muted"> · {files.length}</span>}
          </h2>
          {hint && <p className="sub">{hint}</p>}
        </div>
        {canWrite && (
          <div className="actions">
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              <Icon name="plus" size={14} />
              Add file
            </button>
          </div>
        )}
      </header>
      {canWrite && (
        <input
          ref={input}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => send(e.target.files)}
        />
      )}
      {/* A drop zone, not a control: everything it does is also on the keyboard-reachable
          "Add file" button above, so it carries a group role rather than pretending to be one. */}
      <fieldset
        aria-label={`${title} — drop files here to upload`}
        className="card-pad"
        onDragOver={(e: DragEvent) => {
          if (!canWrite) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e: DragEvent) => {
          if (!canWrite) return;
          e.preventDefault();
          setOver(false);
          send(e.dataTransfer.files);
        }}
        style={{
          paddingTop: 0,
          border: "none",
          margin: 0,
          minInlineSize: 0,
          outline: over ? "2px dashed var(--accent)" : "none",
          outlineOffset: -6,
          borderRadius: 8,
        }}
      >
        {!files ? (
          <p className="sub">…</p>
        ) : files.length === 0 ? (
          <p className="sub" style={{ margin: "8px 0" }}>
            {canWrite ? "No files yet — drop one here, or use Add file." : "No files."}
          </p>
        ) : (
          <table className="table">
            <tbody>
              {files.map((a) => (
                <tr key={a.id}>
                  <td style={{ width: 28 }}>
                    <Icon name={iconFor(a.contentType, a.fileName)} size={16} />
                  </td>
                  <td>
                    <button type="button" className="link" onClick={() => open(a)}>
                      {a.fileName}
                    </button>
                    {a.isInternal && (
                      <span className="pill" title="Kept internal; not sent out with the document">
                        <i />
                        Internal
                      </span>
                    )}
                    <div className="sm muted">
                      {a.description ? `${a.description} · ` : ""}
                      {fileSize(a.byteSize)}
                      {a.uploadedBy ? ` · ${a.uploadedBy}` : ""}
                      {` · ${new Date(a.createdAt).toLocaleDateString(me.locale ?? undefined)}`}
                    </div>
                  </td>
                  {canWrite && (
                    <td className="r nowrap" style={{ width: 80 }}>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Edit ${a.fileName}`}
                        onClick={() => setEditing(a)}
                      >
                        <Icon name="edit" size={15} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={`Delete ${a.fileName}`}
                        disabled={busy}
                        onClick={async () => {
                          if (!(await confirmDialog(`Delete ${a.fileName}? This cannot be undone.`))) return;
                          run(() => del(`/attachments/${a.id}`), `${a.fileName} deleted`);
                        }}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </fieldset>
      {editing && <EditFile file={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

function EditFile({ file, onClose }: { file: Attachment; onClose: () => void }) {
  const { run, busy } = useAction();
  const [description, setDescription] = useState(file.description ?? "");
  const [isInternal, setInternal] = useState(file.isInternal);
  return (
    <div className="card-pad" style={{ borderTop: "1px solid var(--line)" }}>
      <div className="form-grid" style={{ gridTemplateColumns: "1fr auto auto" }}>
        <label className="field">
          <span>{file.fileName}</span>
          <input
            value={description}
            placeholder="What is this file?"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="field" style={{ alignSelf: "end" }}>
          <span>
            <input type="checkbox" checked={isInternal} onChange={(e) => setInternal(e.target.checked)} />{" "}
            Internal
          </span>
        </label>
        <div className="actions" style={{ alignSelf: "end" }}>
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={busy}
            onClick={async () => {
              const ok = await run(
                () => put(`/attachments/${file.id}`, { description, isInternal }),
                "File updated",
              );
              if (ok) onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
